import { useState, useEffect } from 'react';
import { usePlan } from '../../contexts/PlanContext';
import { api } from '../../lib/api';
import {
  CreditCard, Check, X, ArrowRight, AlertTriangle,
  Clock, Receipt, Info, Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BillingRecord {
  id: string;
  tenant_id: string;
  plan: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_provider: string;
  payment_id: string;
  description: string;
  created_at: string;
}

interface BillingStatus {
  payment_available: boolean;
  provider: string;
  plans: Record<string, { amount: number; label: string; value: string }>;
}

// ---------------------------------------------------------------------------
// Plan configuration (matches shared/plan-features.ts)
// ---------------------------------------------------------------------------

interface PlanInfo {
  key: string;
  name: string;
  price: string;
  priceMonthly: number;
  highlight: boolean;
  description: string;
}

const PLANS: PlanInfo[] = [
  {
    key: 'free',
    name: 'Free',
    price: 'Gratis',
    priceMonthly: 0,
    highlight: false,
    description: 'Perfect om te starten met je portfolio',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '\u20AC 9,99/maand',
    priceMonthly: 999,
    highlight: true,
    description: 'Meer mogelijkheden voor serieuze makers',
  },
  {
    key: 'business',
    name: 'Business',
    price: '\u20AC 24,99/maand',
    priceMonthly: 2499,
    highlight: false,
    description: 'Alles onbeperkt voor professionals',
  },
];

// Feature comparison rows
interface FeatureRow {
  label: string;
  free: string | boolean;
  pro: string | boolean;
  business: string | boolean;
}

const FEATURE_ROWS: FeatureRow[] = [
  { label: 'Websites',           free: '1',         pro: '3',         business: 'Onbeperkt' },
  { label: 'Opslag',             free: '500 MB',    pro: '5 GB',      business: '50 GB' },
  { label: "Pagina's",           free: '3',         pro: 'Onbeperkt', business: 'Onbeperkt' },
  { label: 'Albums',             free: true,        pro: true,        business: true },
  { label: 'Paginabouwer',       free: true,        pro: true,        business: true },
  { label: 'Blog',               free: false,       pro: true,        business: true },
  { label: 'Vertalingen',        free: false,       pro: true,        business: true },
  { label: 'Eigen domein',       free: false,       pro: true,        business: true },
  { label: 'Geavanceerde SEO',   free: false,       pro: true,        business: true },
  { label: 'Handmatige backups', free: false,       pro: true,        business: true },
  { label: 'Backup bewaarperiode', free: '7 dagen', pro: '30 dagen',  business: '90 dagen' },
  { label: 'White label',        free: false,       pro: false,       business: true },
  { label: 'Prioriteit support', free: false,       pro: false,       business: true },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAmount(cents: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function statusBadge(status: BillingRecord['status']) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    pending:  { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'In behandeling' },
    paid:     { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Betaald' },
    failed:   { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Mislukt' },
    refunded: { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Terugbetaald' },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Plan order for comparison
// ---------------------------------------------------------------------------
const PLAN_ORDER: Record<string, number> = { free: 0, pro: 1, business: 2 };

function getPlanOrder(plan: string): number {
  return PLAN_ORDER[plan] ?? 0;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TenantBilling() {
  const { currentPlan, tenants, features, refresh } = usePlan();
  const tenantId = tenants[0]?.id;

  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [history, setHistory] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch billing status and history
  useEffect(() => {
    async function fetchData() {
      try {
        const status = await api.get<BillingStatus>('/billing/status');
        setBillingStatus(status);

        if (tenantId) {
          setHistoryLoading(true);
          try {
            const hist = await api.get<{ records: BillingRecord[] }>(`/billing/history/${tenantId}`);
            setHistory(hist.records);
          } catch {
            // billing_history collection may not exist yet
          }
          setHistoryLoading(false);
        }
      } catch (e: any) {
        // Status endpoint may fail if not yet deployed
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [tenantId]);

  // Handle plan upgrade
  async function handleUpgrade(plan: string) {
    if (!tenantId) return;
    setError('');
    setCheckoutLoading(plan);

    try {
      const result = await api.post<{ checkoutUrl?: string; error?: string; payment_available?: boolean }>(
        '/billing/create-checkout',
        {
          plan,
          tenantId,
          returnUrl: `${window.location.origin}/dashboard/billing`,
        }
      );

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else if (result.payment_available === false) {
        setError('Betalingen worden binnenkort beschikbaar. Neem contact op met de beheerder om je plan handmatig te laten wijzigen.');
      }
    } catch (e: any) {
      if (e.status === 503) {
        setError('Betalingen worden binnenkort beschikbaar. Neem contact op met de beheerder om je plan handmatig te laten wijzigen.');
      } else {
        setError(e.message || 'Er ging iets mis bij het aanmaken van de betaling');
      }
    } finally {
      setCheckoutLoading(null);
    }
  }

  // Handle downgrade to free
  async function handleDowngrade() {
    if (!tenantId) return;
    setError('');
    setDowngradeLoading(true);

    try {
      await api.post('/billing/cancel', { tenantId });
      setShowDowngradeConfirm(false);
      setSuccessMessage('Je abonnement is gewijzigd naar het gratis plan.');
      await refresh();

      // Refresh history
      try {
        const hist = await api.get<{ records: BillingRecord[] }>(`/billing/history/${tenantId}`);
        setHistory(hist.records);
      } catch { /* ignore */ }
    } catch (e: any) {
      setError(e.message || 'Downgrade mislukt');
    } finally {
      setDowngradeLoading(false);
    }
  }

  // Simulate a billing period (based on latest paid record or current date)
  const latestPaid = history.find(r => r.status === 'paid' && r.amount > 0);
  const currentPeriodStart = latestPaid
    ? new Date(latestPaid.created_at)
    : new Date();
  const nextRenewal = new Date(currentPeriodStart);
  nextRenewal.setMonth(nextRenewal.getMonth() + 1);

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-7 bg-gray-100 rounded animate-pulse w-64 mb-2" />
          <div className="h-4 bg-gray-100 rounded animate-pulse w-48" />
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card p-6">
            <div className="h-5 bg-gray-100 rounded animate-pulse w-48 mb-3" />
            <div className="h-4 bg-gray-100 rounded animate-pulse w-72" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Abonnement & Facturatie</h1>
        <p className="text-sm text-gray-500 mt-1">
          Beheer je abonnement en bekijk je betalingsgeschiedenis
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-3">
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Payment not available notice */}
      {billingStatus && !billingStatus.payment_available && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-start gap-3">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Betalingen worden binnenkort beschikbaar</p>
            <p className="mt-1">Online betalingen zijn nog niet actief. Neem contact op met de beheerder om je plan te laten wijzigen.</p>
          </div>
        </div>
      )}

      {/* Current plan overview */}
      <div className="card p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-100 text-brand-600 flex items-center justify-center">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Huidig abonnement</p>
              <p className="text-xl font-bold text-gray-900 capitalize mt-0.5">{currentPlan}</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {PLANS.find(p => p.key === currentPlan)?.price || 'Gratis'}
              </p>
            </div>
          </div>
          {currentPlan !== 'free' && (
            <div className="text-right text-sm text-gray-500">
              <p>Volgende verlenging</p>
              <p className="font-medium text-gray-900">{formatDate(nextRenewal.toISOString())}</p>
            </div>
          )}
        </div>

        {/* Quick stats from features */}
        {features && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Websites</p>
              <p className="text-sm font-semibold text-gray-900">
                {features.max_sites === -1 ? 'Onbeperkt' : features.max_sites}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Opslag</p>
              <p className="text-sm font-semibold text-gray-900">
                {features.max_storage_bytes >= 1024 * 1024 * 1024
                  ? `${Math.round(features.max_storage_bytes / (1024 * 1024 * 1024))} GB`
                  : `${Math.round(features.max_storage_bytes / (1024 * 1024))} MB`}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Pagina's</p>
              <p className="text-sm font-semibold text-gray-900">
                {features.max_pages === -1 ? 'Onbeperkt' : features.max_pages}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Backup bewaarperiode</p>
              <p className="text-sm font-semibold text-gray-900">
                {features.backup_retention_days} dagen
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Plan comparison */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Beschikbare abonnementen</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map(plan => {
            const isCurrent = plan.key === currentPlan;
            const isUpgrade = getPlanOrder(plan.key) > getPlanOrder(currentPlan);
            const isDowngrade = getPlanOrder(plan.key) < getPlanOrder(currentPlan);

            return (
              <div
                key={plan.key}
                className={`card p-6 relative ${
                  plan.highlight && !isCurrent ? 'ring-2 ring-brand-500' : ''
                } ${isCurrent ? 'ring-2 ring-green-500 bg-green-50/30' : ''}`}
              >
                {plan.highlight && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-600 text-white text-xs font-medium px-3 py-0.5 rounded-full">
                    Populair
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-medium px-3 py-0.5 rounded-full">
                    Huidig plan
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{plan.price}</p>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                </div>

                {/* Feature list for this plan */}
                <ul className="space-y-2 mb-6">
                  {FEATURE_ROWS.map(row => {
                    const val = row[plan.key as keyof FeatureRow];
                    const included = val === true || (typeof val === 'string' && val !== '');
                    const excluded = val === false;

                    return (
                      <li key={row.label} className="flex items-center gap-2 text-sm">
                        {excluded ? (
                          <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        ) : (
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        )}
                        <span className={excluded ? 'text-gray-400' : 'text-gray-700'}>
                          {row.label}
                          {typeof val === 'string' && val !== '' && (
                            <span className="text-gray-500 ml-1">({val})</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* Action button */}
                <div className="mt-auto">
                  {isCurrent ? (
                    <div className="w-full py-2.5 text-sm text-center font-medium text-green-700 bg-green-100 rounded-lg">
                      Jouw huidige plan
                    </div>
                  ) : isUpgrade ? (
                    <button
                      onClick={() => handleUpgrade(plan.key)}
                      disabled={!!checkoutLoading}
                      className="w-full py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {checkoutLoading === plan.key ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Bezig...</>
                      ) : (
                        <><ArrowUpRight className="w-4 h-4" /> Upgraden naar {plan.name}</>
                      )}
                    </button>
                  ) : isDowngrade ? (
                    <button
                      onClick={() => setShowDowngradeConfirm(true)}
                      className="w-full py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowDownRight className="w-4 h-4" /> Downgraden
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature comparison table (desktop) */}
      <div className="card overflow-hidden hidden md:block">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Functies vergelijken</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 font-medium text-gray-500">Functie</th>
                {PLANS.map(p => (
                  <th key={p.key} className={`text-center px-6 py-3 font-medium ${p.key === currentPlan ? 'text-brand-600 bg-brand-50/50' : 'text-gray-900'}`}>
                    {p.name}
                    {p.key === currentPlan && (
                      <span className="ml-1 text-xs text-brand-500 font-normal">(huidig)</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row, idx) => (
                <tr key={row.label} className={idx % 2 === 0 ? 'bg-gray-50/50' : ''}>
                  <td className="px-6 py-3 text-gray-700">{row.label}</td>
                  {PLANS.map(p => {
                    const val = row[p.key as keyof FeatureRow];
                    return (
                      <td key={p.key} className={`text-center px-6 py-3 ${p.key === currentPlan ? 'bg-brand-50/50' : ''}`}>
                        {val === true ? (
                          <Check className="w-4 h-4 text-green-500 mx-auto" />
                        ) : val === false ? (
                          <X className="w-4 h-4 text-gray-300 mx-auto" />
                        ) : (
                          <span className="text-gray-900 font-medium">{val}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Price row */}
              <tr className="border-t border-gray-200 bg-gray-50">
                <td className="px-6 py-4 font-semibold text-gray-900">Prijs</td>
                {PLANS.map(p => (
                  <td key={p.key} className={`text-center px-6 py-4 font-bold text-gray-900 ${p.key === currentPlan ? 'bg-brand-50/50' : ''}`}>
                    {p.price}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment history */}
      <div className="card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Betalingsgeschiedenis
          </h2>
        </div>

        {historyLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : history.length === 0 ? (
          <div className="p-6 text-center">
            <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nog geen betalingen</p>
            <p className="text-xs text-gray-400 mt-1">Betalingen verschijnen hier zodra je een abonnement afsluit</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="px-6 py-3 font-medium text-gray-500">Datum</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Omschrijving</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Bedrag</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(record => (
                  <tr key={record.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-gray-700">{formatDate(record.created_at)}</td>
                    <td className="px-6 py-3 text-gray-900">{record.description}</td>
                    <td className="px-6 py-3 text-gray-900 font-medium">{formatAmount(record.amount)}</td>
                    <td className="px-6 py-3">{statusBadge(record.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Downgrade confirmation modal */}
      {showDowngradeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Downgraden naar Free?</h3>
                <p className="text-sm text-gray-500">Dit kan niet direct ongedaan worden gemaakt.</p>
              </div>
            </div>

            <div className="mb-4 space-y-3">
              <p className="text-sm text-gray-600">
                Bij downgrade naar het gratis plan verlies je de volgende functies:
              </p>
              <ul className="space-y-1.5">
                {FEATURE_ROWS.filter(row => {
                  const currentVal = row[currentPlan as keyof FeatureRow];
                  const freeVal = row.free;
                  return currentVal !== freeVal && (freeVal === false || typeof currentVal === 'string');
                }).map(row => (
                  <li key={row.label} className="flex items-center gap-2 text-sm text-red-600">
                    <X className="w-3.5 h-3.5 flex-shrink-0" />
                    {row.label}
                    {typeof row[currentPlan as keyof FeatureRow] === 'string' && (
                      <span className="text-gray-400 ml-auto text-xs">
                        {row[currentPlan as keyof FeatureRow] as string} {'->'} {typeof row.free === 'string' ? row.free : 'niet beschikbaar'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDowngradeConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleDowngrade}
                disabled={downgradeLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {downgradeLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Bezig...</>
                ) : (
                  'Bevestig downgrade'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
